import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { customerApi, ApiError, CustomerAccount, CustomerInfoResponse, registerUnauthorizedHandler } from '@vmgate/common';

interface AuthContextValue {
  account: CustomerAccount | null;
  profile: CustomerInfoResponse | null;
  loading: boolean;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [account, setAccount] = useState<CustomerAccount | null>(null);
  const [profile, setProfile] = useState<CustomerInfoResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const loadAccount = useCallback(async () => {
    try {
      const session = await customerApi.getSession();
      setAccount(session);
      const info = await customerApi.getCurrentCustomer();
      setProfile(info);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setAccount(null);
        setProfile(null);
      } else {
        console.warn('Failed to load customer profile', error);
      }
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    const bootstrap = async () => {
      try {
        await loadAccount();
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    bootstrap();
    return () => {
      isMounted = false;
    };
  }, [loadAccount]);

  const login = useCallback(async (credentials: { email: string; password: string }) => {
    setLoading(true);
    try {
      const accountResponse = await customerApi.login(credentials);
      setAccount(accountResponse);
      const profileResponse = await customerApi.getCustomerInfo(accountResponse.customer_id);
      setProfile(profileResponse);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await customerApi.logout();
    } catch (error) {
      console.warn('Customer logout failed', error);
    } finally {
      setAccount(null);
      setProfile(null);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!account) return;
    try {
      const profileResponse = await customerApi.getCurrentCustomer();
      setProfile(profileResponse);
    } catch (error) {
      console.warn('Failed to refresh profile', error);
    }
  }, [account]);

  const value = useMemo<AuthContextValue>(
    () => ({
      account,
      profile,
      loading,
      login,
      logout,
      refreshProfile
    }),
    [account, profile, loading, login, logout, refreshProfile]
  );

  useEffect(() => {
    const unsubscribe = registerUnauthorizedHandler(() => {
      setAccount(null);
      setProfile(null);
      sessionStorage.setItem('vmgate:customerAuthMessage', 'Session expired. Please sign in again.');
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    });
    return unsubscribe;
  }, []);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return ctx;
}

